// touch: force xeito-web Forgejo deploy after lockfile hotfix (2026-05-27)
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { reactErrorHandler } from '@sentry/react'
import { RouterProvider } from '@tanstack/react-router'

class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
    console.error('[RootErrorBoundary]', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f', color: '#fff', fontFamily: 'monospace', flexDirection: 'column', gap: 16, padding: 32 }}>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <h1 style={{ fontSize: 20, margin: 0 }}>Something went wrong</h1>
          <p style={{ color: '#a0a0b0', margin: 0, textAlign: 'center' }}>{this.state.error.message}</p>
          <button onClick={() => window.location.reload()} style={{ padding: '8px 24px', background: '#00f0ff', color: '#0a0a0f', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontWeight: 'bold' }}>
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
import { I18nextProvider } from 'react-i18next'
import { Toaster } from 'sonner'
import { createRouter } from './router'
import { installStaleChunkRecoveryForBrowser } from './lib/stale-chunk-recovery'
import { AuthProvider } from './lib/auth-context'
import { RouterLocationProvider } from './lib/router-location-context'
import { FeatureFlagProvider } from './contexts/FeatureFlagContext'
import { QueryClientProvider } from '@tanstack/react-query'
import { createQueryClient } from './lib/query-client'
import { httpBatchLink } from '@trpc/client'
import { trpc } from '~/utils/trpc'

// Import styles
import './styles/app.css'

// Initialize i18n (must be before any components that use translations)
import i18n from './lib/i18n'

// B-new-2259: GA4 loads only after analytics consent. Restore a prior grant
// from the v2 `xeito-consent` cookie; nothing is loaded for anyone else.
// Without a grant, expire any `_ga*` cookies left by the old pre-consent
// loader (nothing is loaded, so this only deletes cookies).
import { readAnalyticsConsent, loadAnalytics, revokeAnalytics } from './lib/consent-analytics'
if (readAnalyticsConsent(document.cookie)) {
  loadAnalytics(import.meta.env.VITE_GOOGLE_ANALYTICS_ID)
} else {
  revokeAnalytics(import.meta.env.VITE_GOOGLE_ANALYTICS_ID)
}

// B-new-9382: recover from stale lazy chunks after a deploy (one reload per build)
installStaleChunkRecoveryForBrowser()

// Create router first — TanStack integration requires the instance before Sentry.init()
const router = createRouter()

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  // @sentry/core DSN_REGEX uses \w+ for the key, which rejects hyphens.
  // Glitchtip generates UUID-format keys (e.g. "b2da0f33-1f26-…"). Strip
  // hyphens so the validator accepts it — Django's UUID lookup normalises
  // both formats, so Glitchtip receives and matches the stripped key fine.
  const dsn = import.meta.env.VITE_SENTRY_DSN.replace(
    /(https?:\/\/)([\w-]+)(@)/,
    (_: string, proto: string, key: string, at: string) => proto + key.replace(/-/g, '') + at,
  )
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: true,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/api\.xeito\.ai/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

const API_URL = import.meta.env.VITE_API_URL || ''

// Phase 4: BetterAuth's session cookie is HttpOnly (JS can't read it), so we
// rely on credentials: 'include' to ship it with every tRPC request. The
// cookie is scoped to .xeito.ai so it reaches api.xeito.ai automatically.
const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      fetch(input, init) {
        return fetch(input, { ...init, credentials: 'include' })
      },
    }),
  ],
})

// Create query client outside component (stable reference). The factory owns
// the global mutation-error policy — see src/lib/query-client.ts (B-new-85).
const queryClient = createQueryClient()

// Render the app - wrap in a custom element to avoid SSR issues
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: reactErrorHandler(),
    onCaughtError: reactErrorHandler(),
    onRecoverableError: reactErrorHandler(),
  })
  root.render(
    <React.StrictMode>
      <RootErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
              {/* B-new-78: this provider was never mounted, so any page
                  calling the FeatureFlagContext HOOKS threw at render time
                  (the paid /dashboard/reports hub white-screened for every
                  signed-in user).

                  It sits INSIDE AuthProvider, not outside (B-new-100):
                  `featureFlags.list` is a protectedDbProcedure, so mounting it
                  above the auth context fired an authenticated,
                  DB-initialising request — and took a 401 — on every page load
                  for every signed-out visitor, including /sign-in, /sign-up and
                  the 404 splat. Nested here it can read useAuth() and skip the
                  query until there is a session. It still only needs tRPC +
                  QueryClient above it, both of which remain outside. */}
              {/* B-new-1010 — same `router` instance passed to
                  `RouterProvider` below, handed up so `AuthProvider` ->
                  `TermsAcceptanceGate` can read a reactive pathname without
                  itself sitting inside `RouterProvider`'s context (which
                  doesn't exist yet at this point in the tree). This
                  provider is a pure context wrapper — it fires no queries —
                  so it doesn't reintroduce the B-new-100 problem
                  (`protectedDbProcedure` calls above auth for a signed-out
                  visitor). */}
              <RouterLocationProvider router={router}>
                <AuthProvider>
                  <FeatureFlagProvider>
                    <RouterProvider router={router} />
                  </FeatureFlagProvider>
                </AuthProvider>
              </RouterLocationProvider>
            </QueryClientProvider>
          </trpc.Provider>
        </I18nextProvider>
        <Toaster
          position="top-right"
          richColors
          expand={false}
          duration={4000}
          closeButton
        />
      </RootErrorBoundary>
    </React.StrictMode>
  )
}
